import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faTable, 
  faChartBar, 
  faDownload,
  faInfoCircle,
  faSearch,
  faFilter
} from '@fortawesome/free-solid-svg-icons'
import './SimilarityMatrixDisplay.css'

const SimilarityMatrixDisplay = ({ 
  similarityMatrix, 
  professors, 
  questionPapers, 
  summaryStats,
  onDownload 
}) => {
  const [viewMode, setViewMode] = useState('heatmap') // 'heatmap' or 'table'
  const [searchTerm, setSearchTerm] = useState('')
  const [filterThreshold, setFilterThreshold] = useState(0)

  // Get color for similarity score (heatmap) - Better visibility on dark background
  const getScoreColor = (score) => {
    if (score >= 0.8) return '#00ff88' // High similarity - bright green
    if (score >= 0.6) return '#ffdd00' // Medium-high similarity - bright yellow  
    if (score >= 0.4) return '#ff8800' // Medium similarity - bright orange
    if (score >= 0.2) return '#ff4444' // Low similarity - bright red
    return '#888888' // Very low similarity - gray
  }

  // Get color intensity for background with better contrast
  const getBackgroundColor = (score) => {
    if (score >= 0.8) return 'rgba(0, 255, 136, 0.8)' // High similarity - bright green with high opacity
    if (score >= 0.6) return 'rgba(255, 221, 0, 0.8)' // Medium-high similarity - bright yellow
    if (score >= 0.4) return 'rgba(255, 136, 0, 0.8)' // Medium similarity - bright orange
    if (score >= 0.2) return 'rgba(255, 68, 68, 0.8)' // Low similarity - bright red
    return 'rgba(136, 136, 136, 0.6)' // Very low similarity - gray with medium opacity
  }

  // Filter data based on search and threshold
  const getFilteredData = () => {
    if (!professors || !questionPapers || !similarityMatrix) return { professors: [], questionPapers: [], matrix: [] }
    
    let filteredProfessors = professors
    let filteredQuestionPapers = questionPapers
    let filteredMatrix = similarityMatrix

    // Apply search filter
    if (searchTerm) {
      const profIndices = []
      const paperIndices = []
      
      professors.forEach((prof, index) => {
        if (prof.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            prof.department.toLowerCase().includes(searchTerm.toLowerCase())) {
          profIndices.push(index)
        }
      })
      
      questionPapers.forEach((paper, index) => {
        if (paper.course_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            paper.course_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            paper.department.toLowerCase().includes(searchTerm.toLowerCase())) {
          paperIndices.push(index)
        }
      })

      if (profIndices.length > 0 || paperIndices.length > 0) {
        filteredProfessors = profIndices.length > 0 ? 
          profIndices.map(i => professors[i]) : professors
        filteredQuestionPapers = paperIndices.length > 0 ? 
          paperIndices.map(i => questionPapers[i]) : questionPapers
        
        const profFilter = profIndices.length > 0 ? profIndices : professors.map((_, i) => i)
        const paperFilter = paperIndices.length > 0 ? paperIndices : questionPapers.map((_, i) => i)
        
        filteredMatrix = profFilter.map(i => 
          paperFilter.map(j => similarityMatrix[i][j])
        )
      }
    }

    return {
      professors: filteredProfessors,
      questionPapers: filteredQuestionPapers,
      matrix: filteredMatrix
    }
  }

  const { professors: displayProfessors, questionPapers: displayPapers, matrix: displayMatrix } = getFilteredData()

  // Download handlers
  const downloadCSV = () => {
    if (!displayProfessors || !displayPapers || !displayMatrix) return

    let csvContent = 'Professor,'
    csvContent += displayPapers.map(paper => `"${paper.course_code} - ${paper.course_title}"`).join(',') + '\n'
    
    displayMatrix.forEach((row, i) => {
      csvContent += `"${displayProfessors[i].name}",`
      csvContent += row.map(score => score.toFixed(3)).join(',') + '\n'
    })

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'professor_course_similarity_matrix.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const downloadReport = () => {
    if (!summaryStats) return

    let reportContent = 'Similarity Matrix Analysis Report\n'
    reportContent += '=====================================\n\n'
    reportContent += `Generated: ${summaryStats.generated_at}\n`
    reportContent += `Total Professors: ${summaryStats.total_professors}\n`
    reportContent += `Total Courses: ${summaryStats.total_papers}\n`
    reportContent += `Matrix Shape: ${summaryStats.matrix_shape.join(' x ')}\n\n`
    reportContent += `Statistics:\n`
    reportContent += `- Average Similarity: ${summaryStats.average_similarity.toFixed(3)}\n`
    reportContent += `- Maximum Similarity: ${summaryStats.max_similarity.toFixed(3)}\n`
    reportContent += `- Minimum Similarity: ${summaryStats.min_similarity.toFixed(3)}\n`
    reportContent += `- Standard Deviation: ${summaryStats.std_similarity.toFixed(3)}\n\n`
    
    if (summaryStats.top_matches) {
      reportContent += 'Top Matches:\n'
      summaryStats.top_matches.slice(0, 10).forEach((match, i) => {
        reportContent += `${i + 1}. ${match.professor_name} -> ${match.paper_title} (${match.similarity_score.toFixed(3)})\n`
      })
    }

    const blob = new Blob([reportContent], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'similarity_analysis_report.txt'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  if (!displayProfessors.length || !displayPapers.length || !displayMatrix.length) {
    return (
      <div className="similarity-matrix-display">
        <div className="no-data">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>No similarity matrix data available</span>
        </div>
      </div>
    )
  }

  return (
    <div className="similarity-matrix-display">
      <div className="matrix-header">
        <h3>
          <FontAwesomeIcon icon={faChartBar} className="title-icon" />
          Similarity Matrix Results
        </h3>
        
        <div className="matrix-controls">
          <div className="view-toggle">
            <button 
              className={viewMode === 'heatmap' ? 'active' : ''}
              onClick={() => setViewMode('heatmap')}
            >
              <FontAwesomeIcon icon={faChartBar} />
              Heatmap
            </button>
            <button 
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
            >
              <FontAwesomeIcon icon={faTable} />
              Table
            </button>
          </div>
          
          <div className="download-buttons">
            <button onClick={downloadCSV} className="download-btn">
              <FontAwesomeIcon icon={faDownload} />
              CSV
            </button>
            <button onClick={downloadReport} className="download-btn">
              <FontAwesomeIcon icon={faDownload} />
              Report
            </button>
          </div>
        </div>
      </div>

      <div className="matrix-filters">
        <div className="search-filter">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search professors or courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="threshold-filter">
          <FontAwesomeIcon icon={faFilter} className="filter-icon" />
          <label>Min Similarity:</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={filterThreshold}
            onChange={(e) => setFilterThreshold(parseFloat(e.target.value))}
            className="threshold-slider"
          />
          <span>{filterThreshold.toFixed(1)}</span>
        </div>
      </div>

      {summaryStats && (
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Average:</span>
            <span className="stat-value">{summaryStats.average_similarity.toFixed(3)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Maximum:</span>
            <span className="stat-value">{summaryStats.max_similarity.toFixed(3)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Minimum:</span>
            <span className="stat-value">{summaryStats.min_similarity.toFixed(3)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Std Dev:</span>
            <span className="stat-value">{summaryStats.std_similarity.toFixed(3)}</span>
          </div>
        </div>
      )}

      <div className={`matrix-container ${viewMode}`}>
        {viewMode === 'heatmap' ? (
          <div className="heatmap-container">
            <div className="heatmap-table">
              <table>
                <thead>
                  <tr>
                    <th className="paper-header">Course</th>
                    {displayProfessors.map((professor, index) => (
                      <th key={index} className="professor-header" title={professor.name}>
                        {professor.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayPapers.map((paper, i) => (
                    <tr key={i}>
                      <td className="paper-cell" title={`${paper.course_code} - ${paper.course_title}`}>
                        <div style={{ fontWeight: 'bold' }}>{paper.course_code}</div>
                        <div style={{ fontSize: '0.85em', color: '#999' }}>{paper.course_title}</div>
                      </td>
                      {displayProfessors.map((professor, j) => (
                        <td 
                          key={j} 
                          className="similarity-cell"
                          style={{ 
                            backgroundColor: getBackgroundColor(displayMatrix[j][i]),
                            color: displayMatrix[j][i] > 0.5 ? 'white' : '#2c3e50'
                          }}
                          title={`${paper.course_code} (${paper.course_title}) -> ${professor.name}: ${displayMatrix[j][i].toFixed(3)}`}
                        >
                          {displayMatrix[j][i].toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="color-legend">
              <span>Similarity Scale:</span>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: '#888888' }}></div>
                  <span>0.0-0.2</span>
                </div>
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: '#ff4444' }}></div>
                  <span>0.2-0.4</span>
                </div>
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: '#ff8800' }}></div>
                  <span>0.4-0.6</span>
                </div>
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: '#ffdd00' }}></div>
                  <span>0.6-0.8</span>
                </div>
                <div className="legend-item">
                  <div className="color-box" style={{ backgroundColor: '#00ff88' }}></div>
                  <span>0.8-1.0</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Professor</th>
                  <th>Course</th>
                  <th>Similarity Score</th>
                </tr>
              </thead>
              <tbody>
                {displayProfessors.map((professor, i) =>
                  displayPapers.map((paper, j) => {
                    const score = displayMatrix[i][j]
                    if (score < filterThreshold) return null
                    
                    return (
                      <tr key={`${i}-${j}`}>
                        <td>
                          <div className="professor-info">
                            <strong>{professor.name}</strong>
                            <span className="department">{professor.department}</span>
                          </div>
                        </td>
                        <td>
                          <div className="paper-info">
                            <strong>{paper.course_code}</strong>
                            <span className="course-code" style={{ fontSize: '0.9em', color: '#999' }}>{paper.course_title}</span>
                          </div>
                        </td>
                        <td>
                          <span 
                            className="score-badge"
                            style={{ 
                              backgroundColor: getScoreColor(score),
                              color: 'white'
                            }}
                          >
                            {score.toFixed(3)}
                          </span>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default SimilarityMatrixDisplay